import React, { useCallback, useMemo, useState } from 'react';
import { Stack, IconButton, CircularProgress } from '@mui/material';
import { ErrorOutline as ErrorOutlineIcon } from '@material-ui/icons';
import { DeleteOutline as DeleteIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import styled from 'styled-components';
import he from 'he';
import GoalServices from '../../../../../services/goal.services';
import { CascadeKrItem as CascadeKrData, CascadeKrListProps } from './cascadeKrTypes';
import { CascadeKrDeleteModal } from './cascadeKrDeleteModal';
import { TEMP_ID_PREFIX } from './cascadeKrCreateModal';

const Row = styled('tr')`
  & .owner-cell {
    padding-left: 13px;
  }
  & .name-cell {
    color: #333943;
  }
  &:hover {
    border-radius: 4px;
    background: #f7f9fa;
  }
  & .action-buttons {
    display: flex;
    gap: 4px;
    align-items: center;
  }
`;

type CascadeKrItemProps = {
  item: CascadeKrData;
} & Pick<CascadeKrListProps, 'onDelete' | 'onEdit' | 'onValidationClick'>;

export const CascadeKrItem = ({
  item,
  onEdit,
  onDelete,
  onValidationClick,
}: CascadeKrItemProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const goalServices = useMemo(() => new GoalServices(), []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleEdit = useCallback(() => {
    onEdit(item);
  }, [item, onEdit]);

  const handleDelete = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleValidationClick = useCallback(() => {
    if (item.validation) {
      onValidationClick(item.validation);
    }
  }, [item.validation, onValidationClick]);

  const isPersisted = Boolean(item.id && !item.id.startsWith(TEMP_ID_PREFIX));

  const handleSubmitDeleteModal = useCallback(async () => {
    setShowDeleteModal(false);
    if (!isPersisted) {
      onDelete(item);
      return;
    }
    try {
      setIsDeleting(true);
      await goalServices.deleteMetric({ metricId: item.id });
      onDelete(item);
    } catch (error) {
      try {
        await goalServices.logDataDog('Failed to delete cascade key result');
      } catch (loggingError) {
        // swallow logging error
      }
    } finally {
      setIsDeleting(false);
    }
  }, [goalServices, isPersisted, item, onDelete]);

  const ownerCellText = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <span>{he.decode(item.owner?.name) || 'User'}</span>
      {item.validation && (
        <IconButton size="small" color="error" onClick={handleValidationClick}>
          <ErrorOutlineIcon />
        </IconButton>
      )}
    </Stack>
  );

  const isNewItem = !item.id || item.id.startsWith(TEMP_ID_PREFIX);
  const isDisabled = isDeleting;
  const iconButtonProps = !isNewItem ? {
    component: 'a',
    href: `/wb/goal/krDetails?id=${item.id}&edit=true`,
    target: '_blank',
  } : {
    onClick: handleEdit,
    disabled: isDisabled,
  };

  return (
    <Row key={item.id}>
      <td className="owner-cell">{ownerCellText}</td>
      <td className="name-cell">{he.decode(item.name)}</td>
      <td>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>{item.target}</span>
          <div className="action-buttons">
            <IconButton
              size="small"
              title="Edit"
              disabled={isDisabled}
              {...iconButtonProps}
            >
              <EditIcon />
            </IconButton>
            <IconButton onClick={handleDelete} size="small" title="Delete" disabled={isDisabled}>
              {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            </IconButton>
            {showDeleteModal && (
              <CascadeKrDeleteModal
                onClose={handleCloseDeleteModal}
                onSubmit={handleSubmitDeleteModal}
                subCascadeKrCount={item.cascade_children_count}
              />
            )}
          </div>
        </Stack>
      </td>
    </Row>
  );
};
